/**
 * Form Button Components
 *
 * Specialized buttons for form actions with consistent styling and behavior.
 *
 * - SubmitButton: Primary action (Submit, Save, Create, Update) - Always blue
 * - CancelButton: Secondary action (Cancel, Close, Back) - Outline/Ghost
 * - DeleteButton: Destructive action (Delete, Remove) - Red
 *
 * Usage:
 *   <SubmitButton type="submit" isLoading={isSubmitting}>
 *     Save Changes
 *   </SubmitButton>
 *
 *   <CancelButton onClick={onCancel}>Cancel</CancelButton>
 *
 *   <DeleteButton onClick={onDelete}>Delete Item</DeleteButton>
 */

import * as React from 'react';
import { Button, ButtonProps } from './button';
import { cn } from '@/lib/utils';

// Base props without variant (controlled by component)
type BaseFormButtonProps = Omit<ButtonProps, 'variant'>;

/**
 * SubmitButton - Primary form action (blue)
 * Used for: Submit, Save, Create, Update, Confirm
 */
export const SubmitButton = React.forwardRef<HTMLButtonElement, BaseFormButtonProps>(
  ({ className, type = 'submit', children, isLoading, ...props }, ref) => {
    return (
      <Button
        ref={ref}
        type={type}
        variant="primary"
        isLoading={isLoading}
        className={cn(
          'bg-mb-blue-500 hover:bg-mb-blue-600 text-white shadow-sm',
          'focus-visible:ring-2 focus-visible:ring-mb-blue-500',
          className
        )}
        {...props}
      >
        {children}
      </Button>
    );
  }
);
SubmitButton.displayName = 'SubmitButton';

/**
 * CancelButton - Secondary form action (outline)
 * Used for: Cancel, Close, Back, Dismiss
 */
export const CancelButton = React.forwardRef<HTMLButtonElement, BaseFormButtonProps>(
  ({ className, type = 'button', children, ...props }, ref) => {
    return (
      <Button
        ref={ref}
        type={type}
        variant="outline"
        className={cn(
          'border-gray-300 hover:bg-gray-50 text-gray-700',
          className
        )}
        {...props}
      >
        {children}
      </Button>
    );
  }
);
CancelButton.displayName = 'CancelButton';

/**
 * DeleteButton - Destructive form action (red)
 * Used for: Delete, Remove, Revoke, Destroy
 */
export const DeleteButton = React.forwardRef<HTMLButtonElement, BaseFormButtonProps>(
  ({ className, type = 'button', children, isLoading, ...props }, ref) => {
    return (
      <Button
        ref={ref}
        type={type}
        variant="destructive"
        isLoading={isLoading}
        className={cn(
          'bg-red-500 hover:bg-red-600 text-white shadow-sm',
          'focus-visible:ring-2 focus-visible:ring-red-500',
          className
        )}
        {...props}
      >
        {children}
      </Button>
    );
  }
);
DeleteButton.displayName = 'DeleteButton';

/**
 * FormButtonGroup - Convenient wrapper for form action buttons
 * Handles spacing and responsive layout
 */
const FormButtonGroup = React.forwardRef<
  HTMLDivElement,
  React.HTMLAttributes<HTMLDivElement> & {
    reverse?: boolean;
  }
>(({ className, children, reverse = false, ...props }, ref) => {
  return (
    <div
      ref={ref}
      className={cn(
        'flex items-center gap-3',
        reverse ? 'flex-row-reverse' : 'flex-row',
        className
      )}
      {...props}
    >
      {children}
    </div>
  );
});
FormButtonGroup.displayName = 'FormButtonGroup';
